// src/App.tsx
// GEOPTIMALISEERD MET CODE SPLITTING
// Geschatte besparing: ~200+ KiB op initial page load

import React, { useState, useEffect, Suspense, lazy, ComponentType } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QuizResult, QuizQuestion } from './types/quiz-types';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import ContactPage from './components/ContactPage';
import LandingPage from './components/LandingPage';
import { ExamLandingPage, ExamOverviewPage } from './components/seo';
import PrivacyPolicy from './legal/PrivacyPolicy';
import CookiePolicy from './legal/CookiePolicy';
import TermsOfService from './legal/TermsOfService';
import FAQPage from './pages/FAQPage';
import NotFoundPage from './pages/NotFoundPage';
import { InformatieOverview, ArticleList, ArticlePage } from './components/informatie';

// ============================================
// LAZY WITH RETRY - Auto-reload bij ChunkLoadError na deployment
// ============================================
const lazyWithRetry = (importFn: () => Promise<{ default: ComponentType<any> }>) =>
  lazy(() =>
    importFn().catch((error: any) => {
      if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
        // Use timestamp to prevent infinite reload loops (10s cooldown)
        const lastReload = parseInt(sessionStorage.getItem('chunk_reload_ts') || '0', 10);
        if (Date.now() - lastReload > 10000) {
          sessionStorage.setItem('chunk_reload_ts', String(Date.now()));
          window.location.reload();
          return new Promise(() => {}); // Never resolves – page is reloading
        }
      }
      throw error;
    })
  );

// ============================================
// LAZY IMPORTS - Alleen laden wanneer nodig
// ============================================

// Admin (alleen voor admins - besparing: ~10.6 KiB)
const AdminDashboard = lazyWithRetry(() => import('./components/admin/AdminDashboard'));

// Payment routes
const PaymentSuccess = lazyWithRetry(() => import('./components/PaymentSuccess'));
const PaymentCancelled = lazyWithRetry(() => import('./components/PaymentCancelled'));
const RegisterPage = lazyWithRetry(() => import('./components/RegisterPage'));

// Core App Components
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const CreditsDisplay = lazyWithRetry(() => import('./components/CreditsDisplay'));
const Progress = lazyWithRetry(() => import('./components/Progress'));
const PricingPage = lazyWithRetry(() => import('./components/pricing/PricingPage'));
const AccountSettings = lazyWithRetry(() => import('./components/AccountSettings'));

// Quiz & Examen (besparing: ~7.2 KiB)
const Quiz = lazyWithRetry(() => import('./components/Quiz'));
const QuizResults = lazyWithRetry(() => import('./components/QuizResults'));
const ProefExamen = lazyWithRetry(() => import('./components/ProefExamen'));
const ProefExamenResults = lazyWithRetry(() => import('./components/ProefExamenResults'));
const QuestionBankGenerator = lazyWithRetry(() => import('./components/QuestionBankGenerator'));

// Games
const GameHub = lazyWithRetry(() => import('./components/games/GameHub'));
const DefinitionDashGame = lazyWithRetry(() => import('./components/games/DefinitionDashGame'));
const WftBlitzGame = lazyWithRetry(() => import('./components/games/WftBlitzGame'));
const GameResults = lazyWithRetry(() => import('./components/games/GameResults'));
const ActiveChallenges = lazyWithRetry(() => import('./components/games/ActiveChallenges'));
const DuelGame = lazyWithRetry(() => import('./components/games/DuelGame'));
const DuelResults = lazyWithRetry(() => import('./components/games/DuelResults'));
const Leaderboard = lazyWithRetry(() => import('./components/leaderboard/Leaderboard'));
const SchadeSurvivor = lazyWithRetry(() => import('./components/games/SchadeSurvivor'));

// Study
const StudyHub = lazyWithRetry(() => import('./components/study/StudyHub'));
const ToetsTermenOverview = lazyWithRetry(() => import('./components/study/ToetsTermenOverview'));
const SamenvattingView = lazyWithRetry(() => import('./components/study/SamenvattingView'));
const SamenvattingReader = lazyWithRetry(() => import('./components/SamenvattingReader'));
const FullSummaryReader = lazyWithRetry(() => import('./components/study/FullSummaryReader'));


// ============================================
// LOADING COMPONENT
// ============================================
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Laden...</p>
    </div>
  </div>
);

// Mini loader voor kleine componenten
const MiniLoader: React.FC = () => (
  <div className="flex items-center justify-center px-4">
    <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-200 border-t-blue-600"></div>
  </div>
);

// ===== RESPONSIVE APP LAYOUT COMPONENT =====
const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Admin check
  const MASTER_ACCOUNTS = [
    'gterelst@gmail.com',
    'henry@adaptto.nl'
  ];
  const isAdmin = MASTER_ACCOUNTS.map(e => e.toLowerCase()).includes(user?.email?.toLowerCase() || '');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [navigate]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Responsive */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Left Side - Logo + Desktop Nav */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
              <button
                onClick={() => navigate('/app/dashboard')}
                className="text-lg sm:text-2xl font-bold text-blue-600 hover:text-blue-700 transition-colors whitespace-nowrap truncate"
              >
                WFT Study Buddy
              </button>
              
              {/* Desktop Navigation Items */}
              <div className="hidden lg:flex items-center space-x-3">
                {/* Admin Link - Desktop */}
                {isAdmin && (
                  <>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => navigate('/admin')}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span>Admin</span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Right Side - Credits + User Info + Actions */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Credits Display - Lazy loaded */}
              <div className="hidden sm:block">
                <Suspense fallback={<MiniLoader />}>
                  <CreditsDisplay showPurchaseButton={true} />
                </Suspense>
              </div>

{/* Desktop Account & Logout Buttons */}
<div className="hidden md:flex items-center space-x-1">
  <button
    onClick={() => navigate('/app/account')}
    className="px-3 lg:px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
  >
    Account
  </button>
  <button
    onClick={handleLogout}
    className="px-3 lg:px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
  >
    Uitloggen
  </button>
</div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Slide-in Panel */}
      <div 
        className={`lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          
          {/* Header met sluit knop */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <span className="text-lg font-bold text-gray-900">WFT Study Buddy</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Menu sluiten"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Account Sectie */}
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Mijn Account</p>
            <p className="text-sm font-medium text-gray-900 truncate mb-3">{user?.email}</p>
            
            {/* Credits Display */}
            <Suspense fallback={
              <div className="flex items-center gap-2 text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                <span className="text-sm">Laden...</span>
              </div>
            }>
              <CreditsDisplay showPurchaseButton={true} />
            </Suspense>
          </div>

          {/* Navigatie Links */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            
            {/* Admin Dashboard - alleen voor admins */}
            {isAdmin && (
              <button
                onClick={() => {
                  navigate('/admin');
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors font-medium"
              >
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Admin Dashboard</span>
              </button>
            )}

            {/* Dashboard */}
            <button
              onClick={() => {
                navigate('/app/dashboard');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>Dashboard</span>
            </button>

            {/* Proefexamen */}
            <button
              onClick={() => {
                navigate('/app/dashboard');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Proefexamen</span>
            </button>

            {/* AI Vragen Genereren */}
            <button
              onClick={() => {
                navigate('/app/ai-generator');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>AI Vragen Genereren</span>
            </button>

            {/* Games */}
            <button
              onClick={() => {
                navigate('/app/games');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Games</span>
            </button>

            {/* Studiemateriaal */}
            <button
              onClick={() => {
                navigate('/app/study');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span>Studiemateriaal</span>
            </button>

            {/* Mijn Voortgang */}
            <button
              onClick={() => {
                navigate('/app/progress');
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>Mijn Voortgang</span>
            </button>

            {/* Account Settings Link */}
<button
  onClick={() => {
    navigate('/app/account');
    setIsMobileMenuOpen(false);
  }}
  className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
>
  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
  <span>Account</span>
</button>

          </nav>

          {/* Footer met Uitloggen */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={() => {
                handleLogout();
                setIsMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Uitloggen</span>
            </button>
          </div>

        </div>
      </div>

      {/* Main Content - Wrapped in Suspense */}
      <Suspense fallback={<LoadingSpinner />}>
        <Outlet />
      </Suspense>
    </div>
  );
};

// ============================================
// PAGE COMPONENTS (behoud originele logica)
// ============================================

// Dashboard Component
const DashboardPage: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Dashboard />
    </Suspense>
  );
};

// Exam Component (Proefexamen + Results)
const ExamPage: React.FC = () => {
  const { examType } = useParams<{ examType: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // 🆕 Check of dit een preview is via URL parameter
  const searchParams = new URLSearchParams(location.search);
  const isPreview = searchParams.get('preview') === 'true';

  const handleExamComplete = (results: any) => {
    try {
      // 🛠️ Sync storage + small delay voor zekerheid
      sessionStorage.setItem('examResults', JSON.stringify({
        ...results,
        isPreview: isPreview // 🆕 Bewaar preview status in results
      }));
      console.log('✅ Stored results in sessionStorage:', !sessionStorage.getItem('examResults'));
      
      // Navigate na een kleine delay
      setTimeout(() => {
        navigate(`/app/exam/${examType}/results`);
      }, 50);
      
    } catch (error) {
      console.error('❌ Error storing results:', error);
      // Fallback naar oude methode
      navigate(`/app/exam/${examType}/results`, { 
        state: { results, examType, isPreview }
      });
    }
  };

  const handleExamCancel = () => {
    navigate('/app/dashboard');
  };

  if (!examType) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ProefExamen
        examType={examType as any}
        onComplete={handleExamComplete}
        onCancel={handleExamCancel}
        isPreview={isPreview}  // 🆕 Pass preview prop
      />
    </Suspense>
  );
};

// Exam Results Component
const ExamResultsPage: React.FC = () => {
  const { examType } = useParams<{ examType: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = React.useRef(false); // ✅ Track of we al geladen hebben

  useEffect(() => {
    // ✅ Voorkom dubbele load in React Strict Mode
    if (hasLoadedRef.current) {
      console.log('⏭️ Already loaded, skipping...');
      return;
    }

    console.log('🔍 ExamResultsPage mounted, checking sessionStorage...');
    
    const storedResults = sessionStorage.getItem('examResults');
    console.log('🔍 Found in sessionStorage:', !!storedResults);
    
    if (storedResults) {
      try {
        const parsedResults = JSON.parse(storedResults);
        console.log('✅ Parsed results successfully:', {
          hasIncorrectQuestions: !!parsedResults.incorrectQuestions,
          incorrectCount: parsedResults.incorrectQuestions?.length
        });
        
        setResults(parsedResults);
        hasLoadedRef.current = true; // ✅ Markeer als geladen
        setLoading(false);
        
        // ✅ Cleanup pas NA succesvolle render
        setTimeout(() => {
          sessionStorage.removeItem('examResults');
          console.log('🧹 Cleaned up sessionStorage after delay');
        }, 100);
        
      } catch (error) {
        console.error('❌ Error parsing exam results:', error);
        navigate('/app/dashboard');
      }
    } else {
      console.log('❌ No results found, redirecting to dashboard');
      navigate('/app/dashboard');
    }
  }, [navigate, examType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!results) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleRetry = () => navigate(`/app/exam/${examType}`);
  const handleDashboard = () => navigate('/app/dashboard');

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <ProefExamenResults
        results={results}
        examType={examType || 'schade-particulier'}
        onRetry={handleRetry}
        onDashboard={handleDashboard}
      />
    </Suspense>
  );
};

// AI Generator Component
const AIGeneratorPage: React.FC = () => {
  const navigate = useNavigate();
  const handleQuestionsGenerated = (questions: QuizQuestion[]) => {
    navigate('/app/ai-quiz', {
      state: { questions }
    });
  };

  const handleCancel = () => {
    navigate('/app/dashboard');
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <QuestionBankGenerator
        onQuestionsGenerated={handleQuestionsGenerated}
        onCancel={handleCancel}
      />
    </Suspense>
  );
};

// AI Quiz Component
const AIQuizPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;
  // Als geen state, redirect naar AI generator
  if (!state?.questions) {
    return <Navigate to="/app/ai-generator" replace />;
  }

  const handleQuizComplete = (result: QuizResult) => {
    navigate('/app/ai-quiz/results', {
      state: { result }
    });
  };

  const handleBack = () => {
    navigate('/app/dashboard');
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Quiz
        customQuestions={state.questions}
        onQuizComplete={handleQuizComplete}
        onBack={handleBack}
      />
    </Suspense>
  );
};

// AI Quiz Results Component
const AIQuizResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;

  // Als geen state, redirect naar dashboard
  if (!state?.result) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleRetakeQuiz = () => {
    navigate('/app/ai-generator');
  };

  const handleBackToDashboard = () => {
    navigate('/app/dashboard');
  };

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <QuizResults
        result={state.result}
        onRetakeQuiz={handleRetakeQuiz}
        onBackToDashboard={handleBackToDashboard}
      />
    </Suspense>
  );
};

// Study Component
const StudyPage: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <StudyHub />
    </Suspense>
  );
};

// Progress Component
const ProgressPage: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Progress />
    </Suspense>
  );
};

// Protected Route Wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// ============================================
// MAIN APP COMPONENT
// ============================================
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          {/* PUBLIC ROUTES - Eager loaded */}
          <Route path="/" element={<LandingPage />} />

          {/* WFT Examens Overzicht */}
          <Route path="/wft-examens" element={<ExamOverviewPage />} />
          
          {/* Individuele Examen Landing Pages */}
          <Route path="/wft-schade-particulier" element={<ExamLandingPage examSlug="wft-schade-particulier" />} />
          <Route path="/wft-schade-zakelijk" element={<ExamLandingPage examSlug="wft-schade-zakelijk" />} />
          <Route path="/wft-basis" element={<ExamLandingPage examSlug="wft-basis" />} />
          <Route path="/wft-hypotheken" element={<ExamLandingPage examSlug="wft-hypotheken" />} />
          <Route path="/wft-vermogen" element={<ExamLandingPage examSlug="wft-vermogen" />} />
          <Route path="/wft-inkomen" element={<ExamLandingPage examSlug="wft-inkomen" />} />
          <Route path="/wft-zorgverzekeringen" element={<ExamLandingPage examSlug="wft-zorgverzekeringen" />} />
          <Route path="/wft-pensioen" element={<ExamLandingPage examSlug="wft-pensioen" />} />
<Route path="/wft-consumptief-krediet" element={<ExamLandingPage examSlug="wft-consumptief-krediet" />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          {/* Informatie Routes (Public) */}
  <Route path="/informatie" element={<InformatieOverview />} />
  <Route path="/informatie/:category" element={<ArticleList />} />
  <Route path="/informatie/:category/:slug" element={<ArticlePage />} />


          {/* Legal Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/terms" element={<TermsOfService />} />

          {/* PAYMENT ROUTES - Lazy loaded */}
          <Route path="/payment-success" element={
            <Suspense fallback={<LoadingSpinner />}>
              <PaymentSuccess />
            </Suspense>
          } />

{/* REGISTER PAGE - Public, Lazy loaded */}
<Route path="/register" element={
  <Suspense fallback={<LoadingSpinner />}>
    <RegisterPage />
  </Suspense>
} />

          <Route path="/payment-cancelled" element={
            <Suspense fallback={<LoadingSpinner />}>
              <PaymentCancelled />
            </Suspense>
          } />

          {/* ADMIN ROUTE - Lazy loaded */}
          <Route path="/admin" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminDashboard />
            </Suspense>
          } />
          <Route path="/admin/:tab" element={
            <Suspense fallback={<LoadingSpinner />}>
              <AdminDashboard />
            </Suspense>
          } />

          {/* PROTECTED APP ROUTES - NESTED ROUTING */}
          <Route path="/app" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            {/* Redirect /app naar /app/dashboard */}
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            
            {/* Dashboard */}
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Game routes - Lazy loaded */}
            <Route path="games" element={
              <Suspense fallback={<LoadingSpinner />}><GameHub /></Suspense>
            } />
            <Route path="games/definition-dash" element={
              <Suspense fallback={<LoadingSpinner />}><DefinitionDashGame /></Suspense>
            } />
            <Route path="games/definition-dash/results" element={
              <Suspense fallback={<LoadingSpinner />}><GameResults /></Suspense>
            } />
            <Route path="games/wft-blitz" element={
              <Suspense fallback={<LoadingSpinner />}><WftBlitzGame /></Suspense>
            } />
            <Route path="games/wft-blitz/results" element={
              <Suspense fallback={<LoadingSpinner />}><GameResults /></Suspense>
            } />

            <Route path="games/schade-survivor" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SchadeSurvivor />
              </Suspense>
            } />

            {/* P2P Challenge routes - Lazy loaded */}
            <Route path="challenges" element={
              <Suspense fallback={<LoadingSpinner />}><ActiveChallenges /></Suspense>
            } />
            <Route path="duel/:duelId" element={
              <Suspense fallback={<LoadingSpinner />}><DuelGame /></Suspense>
            } />
            <Route path="duel/results" element={
              <Suspense fallback={<LoadingSpinner />}><DuelResults /></Suspense>
            } />

            {/* Leaderboard route - Lazy loaded */}
            <Route path="leaderboard" element={
              <Suspense fallback={<LoadingSpinner />}><Leaderboard /></Suspense>
            } />
            
            {/* Examen routes */}
            <Route path="exam/:examType" element={<ExamPage />} />
            <Route path="exam/:examType/results" element={<ExamResultsPage />} />
            
            {/* AI Generator routes */}
            <Route path="ai-generator" element={<AIGeneratorPage />} />
            <Route path="ai-quiz" element={<AIQuizPage />} />
            <Route path="ai-quiz/results" element={<AIQuizResultsPage />} />
            
            {/* Study routes - Lazy loaded */}
            <Route path="study" element={<StudyPage />} />
            <Route path="study/:examType/taak/:taakId" element={
              <Suspense fallback={<LoadingSpinner />}><ToetsTermenOverview /></Suspense>
            } />
            <Route path="study/:examType/samenvattingen" element={
              <Suspense fallback={<LoadingSpinner />}><SamenvattingReader /></Suspense>
            } />
            <Route path="study/:examType/samenvatting/:code" element={
              <Suspense fallback={<LoadingSpinner />}><SamenvattingView /></Suspense>
            } />
            <Route path="study/:examType/full-summary" element={
              <Suspense fallback={<LoadingSpinner />}><FullSummaryReader /></Suspense>
            } />

            {/* Progress route */}
            <Route path="progress" element={<ProgressPage />} />

            {/* Pricing route - Lazy loaded */}
            <Route path="pricing" element={
              <Suspense fallback={<LoadingSpinner />}><PricingPage /></Suspense>
            } />

          {/* Account Settings route - Lazy loaded */}
<Route path="account" element={
  <Suspense fallback={<LoadingSpinner />}><AccountSettings /></Suspense>
} />

          </Route>



          {/* 404 pagina */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;